import moment from 'moment';
import { getEditableField } from '../store/selectors';
import { getDateFormat, getTimezone } from '../../../../infra/session';
import { combineDateAndTime, getScheduledDateTime } from '../../../../infra/ui/time-selector/utils';
import { CampaignSchedule } from '../steps/campaign-configuration/timeline/input-schema';
export var buildRecurrenceRuleFromStore = function (params) {
    var _a, _b, _c, _d, _e, _f;
    var state = params.store.getState();
    var usesCustomScheduleField = getEditableField(state, 'usesCustomSchedule');
    if (!usesCustomScheduleField.value) {
        return {
            recurring: false,
            scheduledAt: null,
        };
    }
    var frequencyField = getEditableField(state, 'frequency');
    var sendTimeField = getEditableField(state, 'sendTime');
    var sendDateField = getEditableField(state, 'sendDate');
    var dateFormat = getEditableField(state, 'dateFormat');
    var endDateField = getEditableField(state, 'endDate');
    var dayOfWeekField = getEditableField(state, 'dayOfWeek');
    var dayOfMonthField = getEditableField(state, 'dayOfMonth');
    var frequency = null;
    var interval = null;
    var byDayOfWeek = [];
    var byDayOfMonth = null;
    var scheduleStartDateTime = null;
    var end = null;
    var timezone = getTimezone(params.session);
    switch (frequencyField.value) {
        case CampaignSchedule.WEEKLY: {
            frequency = 'WEEKLY';
            interval = 1;
            end = (_a = endDateField.value) !== null && _a !== void 0 ? _a : null;
            if (!dayOfWeekField.value) {
                throw new Error('Day of week is required for weekly campaigns');
            }
            byDayOfWeek = [dayOfWeekField.value];
            return {
                recurringFrequency: frequency,
                recurringInterval: interval,
                recurringDayOfWeek: byDayOfWeek,
                recurringStartDate: sendDateField.value ? moment(sendDateField.value).format('YYYY-MM-DD') : null,
                scheduledAt: null,
                recurring: true,
                recurringEndDate: end ? moment(end).format('YYYY-MM-DD') : null,
                recurringTime: (_b = sendTimeField.value) !== null && _b !== void 0 ? _b : '',
                recurringTimezone: timezone,
            };
        }
        case CampaignSchedule.BI_WEEKLY: {
            frequency = 'WEEKLY';
            interval = 2;
            end = (_c = endDateField.value) !== null && _c !== void 0 ? _c : null;
            if (!dayOfWeekField.value) {
                throw new Error('Day of week is required for bi-weekly campaigns');
            }
            byDayOfWeek = [dayOfWeekField.value];
            return {
                recurringFrequency: frequency,
                recurringInterval: interval,
                recurringDayOfWeek: byDayOfWeek,
                recurringStartDate: sendDateField.value ? moment(sendDateField.value).format('YYYY-MM-DD') : null,
                scheduledAt: null,
                recurring: true,
                recurringEndDate: end ? moment(end).format('YYYY-MM-DD') : null,
                recurringTime: (_d = sendTimeField.value) !== null && _d !== void 0 ? _d : '',
                recurringTimezone: timezone,
            };
        }
        case CampaignSchedule.MONTHLY: {
            frequency = 'MONTHLY';
            interval = 1;
            end = (_e = endDateField.value) !== null && _e !== void 0 ? _e : null;
            if (!dayOfMonthField.value) {
                throw new Error('Day of month is required for monthly campaigns');
            }
            byDayOfMonth = [parseInt(dayOfMonthField.value, 10)];
            return {
                recurringFrequency: frequency,
                recurringInterval: interval,
                recurringDayOfMonth: byDayOfMonth,
                recurringStartDate: sendDateField.value ? moment(sendDateField.value).format('YYYY-MM-DD') : null,
                scheduledAt: scheduleStartDateTime,
                recurring: true,
                recurringEndDate: end ? moment(end).format('YYYY-MM-DD') : null,
                recurringTime: (_f = sendTimeField.value) !== null && _f !== void 0 ? _f : '',
                recurringTimezone: timezone,
            };
        }
        case CampaignSchedule.ONE_TIME: {
            frequency = null;
            interval = null;
            if (!sendDateField.value) {
                throw new Error('Send date is required for one-time campaigns');
            }
            scheduleStartDateTime = combineDateAndTime(sendDateField.value, sendTimeField.value, timezone, dateFormat.value);
            return {
                scheduledAt: scheduleStartDateTime,
                recurring: false,
                recurringTimezone: timezone,
            };
        }
        default:
            throw new Error("Unknown campaign frequency: ".concat(frequencyField.value));
    }
};
export var buildStoreTimelineFromApiCampaign = function (_a) {
    var _b, _c, _d;
    var campaign = _a.campaign, session = _a.session;
    var recurrenceRule = campaign.recurrenceRule, scheduledAt = campaign.scheduledAt;
    var hasRecurrenceOrScheduled = !!(recurrenceRule === null || recurrenceRule === void 0 ? void 0 : recurrenceRule.frequency) || !!scheduledAt;
    var dateFormat = getDateFormat(session);
    if (!hasRecurrenceOrScheduled) {
        return {
            usesCustomSchedule: { value: false },
            frequency: { value: CampaignSchedule.ONE_TIME },
            sendTime: { value: null },
            sendDate: { value: null },
            endDate: { value: null },
            dayOfWeek: { value: null },
            dayOfMonth: { value: null },
        };
    }
    if (scheduledAt) {
        var _e = getScheduledDateTime(scheduledAt, getTimezone(session), dateFormat, true), scheduledDate = _e.scheduledDate, scheduledTime = _e.scheduledTime;
        return {
            usesCustomSchedule: { value: true },
            frequency: { value: CampaignSchedule.ONE_TIME },
            sendTime: { value: scheduledTime },
            sendDate: { value: scheduledDate },
            endDate: { value: null },
            dayOfWeek: { value: null },
            dayOfMonth: { value: null },
        };
    }
    // there are two ways to represent weekly recurrence:
    // 1. frequency: DAILY, interval: 7
    // 2. frequency: WEEKLY, interval: 1
    var isEverySevenDays = (recurrenceRule === null || recurrenceRule === void 0 ? void 0 : recurrenceRule.frequency) === 'DAILY' && recurrenceRule.interval === 7;
    var frequency = CampaignSchedule.ONE_TIME;
    if ((recurrenceRule === null || recurrenceRule === void 0 ? void 0 : recurrenceRule.frequency) === 'WEEKLY') {
        frequency = recurrenceRule.interval === 2 ? CampaignSchedule.BI_WEEKLY : CampaignSchedule.WEEKLY;
    }
    else if ((recurrenceRule === null || recurrenceRule === void 0 ? void 0 : recurrenceRule.frequency) === 'MONTHLY') {
        frequency = CampaignSchedule.MONTHLY;
    }
    else if (isEverySevenDays) {
        frequency = CampaignSchedule.WEEKLY;
    }
    var timezone = getTimezone(session);
    var sendDate = (recurrenceRule === null || recurrenceRule === void 0 ? void 0 : recurrenceRule.start) ? moment(recurrenceRule.start).tz(timezone).format(dateFormat) : null;
    var endDate = (recurrenceRule === null || recurrenceRule === void 0 ? void 0 : recurrenceRule.end) ? moment(recurrenceRule.end).tz(timezone).format(dateFormat) : null;
    var dayOfMonth = ((_b = recurrenceRule === null || recurrenceRule === void 0 ? void 0 : recurrenceRule.byDayOfMonth) === null || _b === void 0 ? void 0 : _b.length) === 1 ? String(recurrenceRule.byDayOfMonth[0]) : null;
    var dayOfWeek = ((_c = recurrenceRule === null || recurrenceRule === void 0 ? void 0 : recurrenceRule.byDayOfWeek) === null || _c === void 0 ? void 0 : _c.length) === 1 ? recurrenceRule.byDayOfWeek[0] : null;
    if (isEverySevenDays && !dayOfWeek && sendDate) {
        dayOfWeek = moment(recurrenceRule.start).tz(timezone).format('dd').toUpperCase();
    }
    return {
        usesCustomSchedule: { value: true },
        frequency: { value: frequency },
        sendTime: { value: (_d = recurrenceRule === null || recurrenceRule === void 0 ? void 0 : recurrenceRule.time) !== null && _d !== void 0 ? _d : '' },
        sendDate: { value: sendDate },
        endDate: { value: endDate },
        dayOfWeek: { value: dayOfWeek },
        dayOfMonth: { value: dayOfMonth },
    };
};
